'use client';

import type { FC } from 'react';
import { useLocalTranslation } from '../../../../hooks/useLocalTranslation';
import translations from '../RushMid.translations';
import { STANCE_THEME, type StanceKey } from './stanceTheme';

const STANCES = ['attack', 'maneuver', 'defense'] as const;

const LABEL_KEY = { attack: 'stanceAttack', maneuver: 'stanceManeuver', defense: 'stanceDefense' } as const;

const POS = { attack: { x: 140, y: 48 }, maneuver: { x: 224, y: 152 }, defense: { x: 56, y: 152 } } as const;

const Badge: FC<{ stance: StanceKey; label: string }> = ({ stance, label }) => {
  const { ring, glow, tint, Icon } = STANCE_THEME[stance];
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-full border-2"
        style={{
          borderColor: ring,
          background: `radial-gradient(circle at 35% 30%, ${tint[0]}, ${tint[1]})`,
          boxShadow: `0 0 16px ${glow}`,
        }}
      >
        <Icon size={22} className="text-white" />
      </div>
      <span className="text-xs font-semibold text-white">{label}</span>
    </div>
  );
};

const Arrow: FC<{ from: { x: number; y: number }; to: { x: number; y: number } }> = ({ from, to }) => {
  const cx = 140, cy = 122;
  const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2;
  const dx = mx - cx, dy = my - cy;
  const len = Math.hypot(dx, dy) || 1;
  const bulge = 26;
  const qx = mx + (dx / len) * bulge, qy = my + (dy / len) * bulge;
  const trim = (a: { x: number; y: number }, b: { x: number; y: number }, r: number) => {
    const ddx = b.x - a.x, ddy = b.y - a.y, l = Math.hypot(ddx, ddy) || 1;
    return { x: a.x + (ddx / l) * r, y: a.y + (ddy / l) * r };
  };
  const s = trim(from, { x: qx, y: qy }, 38);
  const e = trim(to, { x: qx, y: qy }, 40);
  return <path d={`M ${s.x} ${s.y} Q ${qx} ${qy} ${e.x} ${e.y}`} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={2.5} markerEnd="url(#rps-arrow)" />;
};

interface RpsDiagramProps {
  compact?: boolean;
}

export const RpsDiagram: FC<RpsDiagramProps> = ({ compact }) => {
  const { t } = useLocalTranslation(translations);

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[210px] w-[280px]">
        <svg viewBox="0 0 280 210" className="absolute inset-0 h-full w-full">
          <defs>
            <marker id="rps-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(255,255,255,0.7)" />
            </marker>
          </defs>
          <Arrow from={POS.attack} to={POS.maneuver} />
          <Arrow from={POS.maneuver} to={POS.defense} />
          <Arrow from={POS.defense} to={POS.attack} />
        </svg>
        {STANCES.map((k) => (
          <div
            key={k}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: POS[k].x, top: POS[k].y - 4 }}
          >
            <Badge stance={k} label={t(LABEL_KEY[k])} />
          </div>
        ))}
      </div>
      {!compact && (
        <div className="mt-1 text-center text-xs text-label-secondary">{t('howtoRpsCaption')}</div>
      )}
    </div>
  );
};
